import { fireEvent, render, waitFor } from '@testing-library/preact';
import { expect } from 'chai';
import { VNode } from 'preact';

import { Metadata, SelectionMode } from '../../../UNSAFE_Collection';
import { RootEnvironmentProvider } from '../../../UNSAFE_Environment';
import type { RootEnvironment } from '../../../UNSAFE_Environment';
import { Gridlines } from '../../../UNSAFE_ListView';
import { Keys } from '../../../utils/UNSAFE_keys';
import { matchTranslationBundle } from '../../../utils/UNSAFE_matchTranslationBundle';

import { populateData } from './listViewTestFixtures';
import type { Employee } from './listViewTestFixtures';
import { getContents, getRoot, mouseCheckboxSelection, mouseItemSelection } from './testHelpers';

type Props<K> = {
  data: { data: Employee<K>; metadata: Metadata<K> }[];
  selectedKeys?: Keys<K>;
  selectionMode?: SelectionMode;
  gridlines?: Gridlines;
  onClick?: () => void;
};

type TestComponentProps<K extends string | number> = (props: Props<K>) => VNode;

export const testSelection = (TestComponent: TestComponentProps<any>) => {
  it('select single - numeric key', async () => {
    const data = populateData(10, true) as Employee<number>[];
    const dataMetadata: { data: Employee<number>; metadata: Metadata<number> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const list = render(
      <TestComponent
        data={dataMetadata}
        selectionMode="single"
        onClick={() => {
          expect(false, 'Click event should not propagate').to.be.true;
        }}></TestComponent>
    );
    const items = await getContents(list);

    mouseItemSelection(items[3]);
    await waitFor(() => {
      expect(items[3].getAttribute('aria-selected')).eq('true');
    });

    mouseItemSelection(items[5]);
    await waitFor(() => {
      expect(items[5].getAttribute('aria-selected')).eq('true');
    });

    mouseItemSelection(items[7]);
    await waitFor(() => {
      expect(items[7].getAttribute('aria-selected')).eq('true');
    });
  });

  it('select multiple items using shift click', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    const list = render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent data={dataMetadata} selectionMode="multiple"></TestComponent>
      </RootEnvironmentProvider>
    );

    const items = await getContents(list);

    mouseItemSelection(items[2]);
    mouseItemSelection(items[5], { shiftKey: true });
    // console.log('here');
    // console.log(list.debug());
    await waitFor(() => {
      expect(items[1].getAttribute('aria-selected')).eq('false');
      expect(items[2].getAttribute('aria-selected')).eq('true');
      expect(items[3].getAttribute('aria-selected')).eq('true');
      expect(items[4].getAttribute('aria-selected')).eq('true');
      expect(items[5].getAttribute('aria-selected')).eq('true');
      expect(items[6].getAttribute('aria-selected')).eq('false');
    });
  });

  it('select multiple items using shift space bar', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    const list = render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent data={dataMetadata} selectionMode="multiple"></TestComponent>
      </RootEnvironmentProvider>
    );

    const items = await getContents(list);

    fireEvent.keyUp(items[2], { key: ' ' });
    fireEvent.keyUp(items[5], { key: ' ', shiftKey: true });
    expect(items[1].getAttribute('aria-selected')).eq('false');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');
    expect(items[4].getAttribute('aria-selected')).eq('true');
    expect(items[5].getAttribute('aria-selected')).eq('true');
    expect(items[6].getAttribute('aria-selected')).eq('false');
  });

  it('selects multiple items when clicking on Selector checkboxes', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    const list = render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent data={dataMetadata} selectionMode="multiple"></TestComponent>
      </RootEnvironmentProvider>
    );

    const items = await getContents(list);

    mouseCheckboxSelection(items[2]);
    await waitFor(() => {
      expect(items[2].getAttribute('aria-selected')).eq('true');
      expect(items[4].getAttribute('aria-selected')).eq('false');
    });
    mouseCheckboxSelection(items[4]);
    await waitFor(() => {
      expect(items[2].getAttribute('aria-selected')).eq('true');
      expect(items[4].getAttribute('aria-selected')).eq('true');
    });
  });

  it('correctly modifies range selection when using SPACE to select', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    const list = render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent data={dataMetadata} selectionMode="multiple"></TestComponent>
      </RootEnvironmentProvider>
    );

    const items = await getContents(list);

    fireEvent.keyUp(items[2], { key: ' ' });
    fireEvent.keyUp(items[5], { key: ' ', shiftKey: true });
    expect(items[1].getAttribute('aria-selected')).eq('false');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');
    expect(items[4].getAttribute('aria-selected')).eq('true');
    expect(items[5].getAttribute('aria-selected')).eq('true');
    expect(items[6].getAttribute('aria-selected')).eq('false');

    // keyboard select to "toggle" one item off
    fireEvent.keyUp(items[3], { key: ' ' });
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('false');
    expect(items[4].getAttribute('aria-selected')).eq('true');

    // keyboard select again to "replace" with single selection only
    fireEvent.keyUp(items[3], { key: ' ' });
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('true');
    expect(items[4].getAttribute('aria-selected')).eq('false');
  });

  it('selects single items using space keypress', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    const list = render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent data={dataMetadata} selectionMode="multiple"></TestComponent>
      </RootEnvironmentProvider>
    );

    const items = await getContents(list);

    fireEvent.keyUp(items[2], { key: ' ' });
    expect(items[1].getAttribute('aria-selected')).eq('false');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('false');

    fireEvent.keyUp(items[3], { key: ' ' });
    expect(items[1].getAttribute('aria-selected')).eq('false');
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('true');
  });

  it('selects single items using enter keypress', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    const list = render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent data={dataMetadata} selectionMode="multiple"></TestComponent>
      </RootEnvironmentProvider>
    );

    const items = await getContents(list);

    fireEvent.keyUp(items[2], { key: 'Enter' });
    expect(items[1].getAttribute('aria-selected')).eq('false');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('false');

    fireEvent.keyUp(items[3], { key: 'Enter' });
    expect(items[1].getAttribute('aria-selected')).eq('false');
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('true');
  });

  it('select multiple items then click on a selected items', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    const list = render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent data={dataMetadata} selectionMode="multiple"></TestComponent>
      </RootEnvironmentProvider>
    );

    const items = await getContents(list);

    mouseItemSelection(items[2]);
    mouseItemSelection(items[5], { shiftKey: true });
    await waitFor(() => {
      expect(items[2].getAttribute('aria-selected')).eq('true');
      expect(items[3].getAttribute('aria-selected')).eq('true');
      expect(items[4].getAttribute('aria-selected')).eq('true');
      expect(items[5].getAttribute('aria-selected')).eq('true');
    });

    mouseItemSelection(items[3]);
    await waitFor(() => {
      expect(items[2].getAttribute('aria-selected')).eq('false');
      expect(items[3].getAttribute('aria-selected')).eq('true');
      expect(items[4].getAttribute('aria-selected')).eq('false');
      expect(items[5].getAttribute('aria-selected')).eq('false');
    });
  });

  it('select multiple items then shift click on a selected items', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    const list = render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent data={dataMetadata} selectionMode="multiple"></TestComponent>
      </RootEnvironmentProvider>
    );

    const items = await getContents(list);

    mouseItemSelection(items[2]);
    mouseItemSelection(items[5], { shiftKey: true });
    await waitFor(() => {
      expect(items[2].getAttribute('aria-selected')).eq('true');
      expect(items[3].getAttribute('aria-selected')).eq('true');
      expect(items[4].getAttribute('aria-selected')).eq('true');
      expect(items[5].getAttribute('aria-selected')).eq('true');
    });

    mouseItemSelection(items[3], { shiftKey: true });
    await waitFor(() => {
      expect(items[2].getAttribute('aria-selected')).eq('true');
      expect(items[3].getAttribute('aria-selected')).eq('true');
      expect(items[4].getAttribute('aria-selected')).eq('false');
      expect(items[5].getAttribute('aria-selected')).eq('false');
    });
  });

  it('select multiple items then metaKey click on a selected items, to select and unselect', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );

    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    const list = render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent data={dataMetadata} selectionMode="multiple"></TestComponent>
      </RootEnvironmentProvider>
    );

    const items = await getContents(list);

    mouseItemSelection(items[2]);
    mouseItemSelection(items[5], { shiftKey: true });
    await waitFor(() => {
      expect(items[2].getAttribute('aria-selected')).eq('true');
      expect(items[3].getAttribute('aria-selected')).eq('true');
      expect(items[4].getAttribute('aria-selected')).eq('true');
      expect(items[5].getAttribute('aria-selected')).eq('true');
    });

    // first click to deselect
    mouseItemSelection(items[3], { metaKey: true });
    await waitFor(() => {
      expect(items[2].getAttribute('aria-selected')).eq('true');
      expect(items[3].getAttribute('aria-selected')).eq('false');
      expect(items[4].getAttribute('aria-selected')).eq('true');
      expect(items[5].getAttribute('aria-selected')).eq('true');
    });

    // second click to reselect
    mouseItemSelection(items[3], { metaKey: true });
    await waitFor(() => {
      expect(items[2].getAttribute('aria-selected')).eq('true');
      expect(items[3].getAttribute('aria-selected')).eq('true');
      expect(items[4].getAttribute('aria-selected')).eq('true');
      expect(items[5].getAttribute('aria-selected')).eq('true');
    });

    // click outside of range should be additive too
    mouseItemSelection(items[7], { metaKey: true });
    await waitFor(() => {
      expect(items[2].getAttribute('aria-selected')).eq('true');
      expect(items[3].getAttribute('aria-selected')).eq('true');
      expect(items[4].getAttribute('aria-selected')).eq('true');
      expect(items[5].getAttribute('aria-selected')).eq('true');
      expect(items[6].getAttribute('aria-selected')).eq('false');
      expect(items[7].getAttribute('aria-selected')).eq('true');
    });
  });

  it('supports multi-select (and deselect) via keyboard arrow keys', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    const list = render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent data={dataMetadata} selectionMode="multiple"></TestComponent>
      </RootEnvironmentProvider>
    );
    const grid = await getRoot(list);
    const items = await getContents(list);

    // select items 1-3
    fireEvent.click(items[0]);
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    fireEvent.keyDown(grid, { key: 'ArrowDown', shiftKey: true });
    fireEvent.keyDown(grid, { key: 'ArrowDown', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('false');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');

    // deselect 2 and 3 via up-arrow
    fireEvent.keyDown(grid, { key: 'ArrowUp', shiftKey: true });
    fireEvent.keyDown(grid, { key: 'ArrowUp', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('false');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('false');

    // continue up past initial anchor, to select items 0 and 1
    fireEvent.keyDown(grid, { key: 'ArrowUp', shiftKey: true });
    fireEvent.keyDown(grid, { key: 'ArrowUp', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('false');
  });

  it('supports extending mouse initiated range select via arrow keys', async () => {
    const data = populateData(100) as Employee<string>[];
    const dataMetadata: { data: Employee<string>; metadata: Metadata<string> }[] = data.map(
      (data) => {
        return { data: data, metadata: { key: data.id } };
      }
    );
    const locale = matchTranslationBundle(['en'], new Set(['en-US', 'en']));
    const { default: translations } = await import(`../../../resources/nls/${locale}/bundle.ts`);
    const env: Partial<RootEnvironment> = {
      translations: { '@oracle/oraclejet-preact': translations }
    };
    const list = render(
      <RootEnvironmentProvider environment={env}>
        <TestComponent data={dataMetadata} selectionMode="multiple"></TestComponent>
      </RootEnvironmentProvider>
    );
    const grid = await getRoot(list);
    const items = await getContents(list);

    // select items 2-5 via click
    mouseItemSelection(items[2]);
    mouseItemSelection(items[5], { shiftKey: true });
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');
    expect(items[4].getAttribute('aria-selected')).eq('true');
    expect(items[5].getAttribute('aria-selected')).eq('true');
    expect(items[6].getAttribute('aria-selected')).eq('false');
    expect(items[7].getAttribute('aria-selected')).eq('false');

    // now extend by two more rows
    fireEvent.keyDown(grid, { key: 'ArrowDown', shiftKey: true });
    fireEvent.keyDown(grid, { key: 'ArrowDown', shiftKey: true });
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');
    expect(items[4].getAttribute('aria-selected')).eq('true');
    expect(items[5].getAttribute('aria-selected')).eq('true');
    expect(items[6].getAttribute('aria-selected')).eq('true');
    expect(items[7].getAttribute('aria-selected')).eq('true');

    // now deselect those via up-arrow
    fireEvent.keyDown(grid, { key: 'ArrowUp', shiftKey: true });
    fireEvent.keyDown(grid, { key: 'ArrowUp', shiftKey: true });
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');
    expect(items[4].getAttribute('aria-selected')).eq('true');
    expect(items[5].getAttribute('aria-selected')).eq('true');
    expect(items[6].getAttribute('aria-selected')).eq('false');
    expect(items[7].getAttribute('aria-selected')).eq('false');
  });
};
